import type { FaceEmbedding } from '../../domain/valueObjects/FaceEmbedding';
import type { DataUriPhoto } from '../../domain/valueObjects/DataUriPhoto';
import { LocalFaceEmbeddingRepository } from '../../infrastructure/repositories/LocalFaceEmbeddingRepository';
import { enqueueSyncOperation } from '@/shared/sync/syncQueue';
import { LocalEmployeeRepository } from '@/modules/employees/infrastructure/repositories/LocalEmployeeRepository';

export type EnrollV2Source =
  | 'auto-after-facial-match'
  | 'auto-after-pin'
  | 'auto-after-card'
  | 'admin-manual';

export interface EnrollV2EmbeddingInput {
  idPerson: number;
  idEntity: number;
  idAccessRole: number;
  embedding: FaceEmbedding;
  photoDataUri: DataUriPhoto;
  source: EnrollV2Source;
}

const localRepo = new LocalFaceEmbeddingRepository();
const localEmployeeRepo = new LocalEmployeeRepository();

/**
 * Persists a 512-d face embedding locally and enqueues a sync to backend.
 * Idempotent: if the employee already has a 512-d embedding cached, no-op.
 * Never throws — failures are logged silently (auto-enrollment is best-effort).
 *
 * After persisting the embedding to `db.faceEmbeddings` (offline matcher cache),
 * also patches the cached employee DTO's `person_embed_v2` field so that the
 * Employee mapper immediately reflects the new enrollment state — critical for
 * offline-just-enrolled UI consistency (the kiosk shows ENROLADO before the
 * sync queue drains to the backend).
 */
export async function enrollV2EmbeddingUseCase(input: EnrollV2EmbeddingInput): Promise<void> {
  try {
    // NOTE: We do NOT short-circuit here anymore. The old guard prevented
    // consecutive enrollments (admin updating face multiple times), causing
    // a bug where:
    // 1st update: works (no existing record)
    // 2nd update: blocked (existing.source = 'local-enrolled', guard returns early)
    // 3rd update: blocked (same reason)
    // Result: UI doesn't update photo, sync queue not enqueued.
    // 
    // Solution: Always allow re-enrollment. The use case is idempotent (upsert),
    // and consecutive enrollments are valid (admin correcting a bad photo, better
    // lighting, etc.). The sync queue will process all updates in order.

    const embeddingVector = input.embedding.toArray();

    await localRepo.upsert({
      idPerson:    input.idPerson,
      idAccessRole: input.idAccessRole,
      embedding:   embeddingVector,
      dim:         512,
      source:      'local-enrolled',
      syncStatus:  'pending',
      updatedAt:   Date.now(),
    });

    // Mirror to db.employees[].raw.person_embed_v2 so toEmployee(raw) reflects
    // the fresh enrollment immediately (offline-first UI). No-op when the
    // employee row is not in the local cache yet.
    await localEmployeeRepo.patchPersonEmbedV2(input.idPerson, embeddingVector);
    
    // Also patch the photo so the UI shows the captured image immediately.
    // We store the Data URI temporarily — when sync completes, the backend
    // will return the real path and overwrite this.
    await localEmployeeRepo.patchPhoto(input.idPerson, input.photoDataUri.value);

    await enqueueSyncOperation({
      module:    'face_embedding_v2',
      operation: 'create',
      endpoint:  `/v1/clock/app/person/${input.idPerson}/enroll/v2/embed`,
      payload: {
        data:        embeddingVector,
        photo:       input.photoDataUri.value,
        source:      input.source,
        captured_at: new Date().toISOString(),
      },
    });
  } catch (err) {
    console.warn('[EnrollV2EmbeddingUseCase] Failed silently:', err);
  }
}
